import React, { useState } from "react";
import { Alert, Button, Form, ProgressBar, Spinner } from "react-bootstrap";

import { retrieveCustomerDetails, retrieveRelatedMeters } from "../../../services/zoho.service";

import {
  BulkCustomer,
  DEFAULT_OFFERS_PER_METER,
  isMeterUsable,
  isValidZohoId,
  parseZohoIds,
  ZOHO_ID_LENGTH,
} from "./bulk.utils";

// How many customers are looked up in Zoho at once, so a big paste doesn't
// fire off hundreds of requests together
const LOOKUP_BATCH_SIZE = 5;

interface LookupProgress {
  done: number;
  total: number;
  meterCount: number;
}

interface LookupSummary {
  customerCount: number;
  meterCount: number;
}

const plural = (count: number, word: string) => `${String(count)} ${word}${count === 1 ? "" : "s"}`;

type LookupResult = { ok: true; customer: BulkCustomer } | { ok: false; error: string };

const lookupCustomer = async (zohoId: string): Promise<LookupResult> => {
  try {
    const customerResult = await retrieveCustomerDetails(zohoId);
    if (!customerResult.success) {
      return {
        ok: false,
        error:
          customerResult.statusCode === 404
            ? `${zohoId}: customer not found`
            : `${zohoId}: ${customerResult.error || "error retrieving customer"}`,
      };
    }

    const metersResult = await retrieveRelatedMeters(zohoId);
    if (!metersResult.success) {
      return {
        ok: false,
        error: `${zohoId}: couldn't retrieve meters (${metersResult.error || "unknown error"})`,
      };
    }
    if (metersResult.data.length === 0) {
      return { ok: false, error: `${zohoId}: customer has no meters` };
    }

    const meters = metersResult.data;
    return {
      ok: true,
      customer: {
        zohoId,
        customerName: customerResult.data.Account_Name,
        offersPerMeter: DEFAULT_OFFERS_PER_METER,
        customerImage: null,
        meters,
        // Everything usable is ticked by default - untick what's not wanted
        selectedMeterIds: meters.filter(isMeterUsable).map((m) => m.id),
      },
    };
  } catch {
    return { ok: false, error: `${zohoId}: error connecting to CRM` };
  }
};

interface BulkCustomerInputProps {
  onRetrieved: (customers: BulkCustomer[]) => void;
  onRetrievingChange: (isRetrieving: boolean) => void;
}

const BulkCustomerInput: React.FC<BulkCustomerInputProps> = ({
  onRetrieved,
  onRetrievingChange,
}) => {
  const [rawIds, setRawIds] = useState("");
  const [isRetrieving, setIsRetrieving] = useState(false);
  const [errors, setErrors] = useState<string[]>([]);
  const [progress, setProgress] = useState<LookupProgress | null>(null);
  const [summary, setSummary] = useState<LookupSummary | null>(null);

  const handleRetrieve = async () => {
    // Already-loaded IDs are fetched again too - the parent refreshes their cards
    const ids = parseZohoIds(rawIds);
    const invalid = ids.filter((id) => !isValidZohoId(id));
    const toLookup = ids.filter(isValidZohoId);

    const newErrors = invalid.map(
      (id) => `${id}: must be exactly ${String(ZOHO_ID_LENGTH)} digits`,
    );
    const found: BulkCustomer[] = [];
    const tally: LookupProgress = { done: 0, total: toLookup.length, meterCount: 0 };

    setIsRetrieving(true);
    onRetrievingChange(true);
    setErrors([]);
    setSummary(null);
    setProgress({ ...tally });
    for (let i = 0; i < toLookup.length; i += LOOKUP_BATCH_SIZE) {
      // Tick the progress as each lookup lands rather than per batch
      await Promise.all(
        toLookup.slice(i, i + LOOKUP_BATCH_SIZE).map(async (id) => {
          const r = await lookupCustomer(id);
          if (r.ok) {
            found.push(r.customer);
            tally.meterCount += r.customer.meters.length;
          } else {
            newErrors.push(r.error);
          }
          tally.done += 1;
          setProgress({ ...tally });
        }),
      );
    }
    setIsRetrieving(false);
    onRetrievingChange(false);
    setProgress(null);

    setErrors(newErrors);
    if (found.length > 0) {
      setSummary({ customerCount: found.length, meterCount: tally.meterCount });
    }
    onRetrieved(found);
    // Keep only the IDs that failed so they can be fixed and retried
    const failedIds = ids.filter((id) => !found.some((c) => c.zohoId === id));
    setRawIds(failedIds.join("\n"));
  };

  return (
    <div className="mb-3" style={isRetrieving ? { cursor: "progress" } : undefined}>
      <Form.Group controlId="bulk-zoho-ids" className="mb-2">
        <Form.Label>Zoho customer IDs</Form.Label>
        <Form.Control
          as="textarea"
          rows={4}
          placeholder="Paste customer IDs - one per line or comma separated"
          value={rawIds}
          onChange={(e) => {
            setRawIds(e.target.value);
          }}
          disabled={isRetrieving}
        />
      </Form.Group>
      <Button
        variant="primary"
        onClick={() => void handleRetrieve()}
        disabled={isRetrieving || !rawIds.trim()}
      >
        {isRetrieving ? (
          <>
            <Spinner as="span" animation="border" size="sm" className="me-2" aria-hidden="true" />
            Retrieving...
          </>
        ) : (
          "Retrieve customers"
        )}
      </Button>

      {progress && (
        <div className="mt-2" role="status">
          <ProgressBar
            animated
            now={progress.total ? (progress.done / progress.total) * 100 : 0}
            style={{ height: "0.5rem" }}
          />
          <small className="text-muted">
            Loaded {progress.done}/{progress.total} customers -{" "}
            {plural(progress.meterCount, "meter")} found
          </small>
        </div>
      )}

      {summary && (
        <Alert
          variant="success"
          className="mt-2 mb-0"
          dismissible
          onClose={() => {
            setSummary(null);
          }}
        >
          ✓ Retrieved {plural(summary.customerCount, "customer")} -{" "}
          {plural(summary.meterCount, "meter")} found
        </Alert>
      )}

      {errors.length > 0 && (
        <Alert
          variant="warning"
          className="mt-2 mb-0"
          dismissible
          onClose={() => {
            setErrors([]);
          }}
        >
          <div>These IDs were skipped:</div>
          <ul className="mb-0">
            {errors.map((e) => (
              <li key={e}>{e}</li>
            ))}
          </ul>
        </Alert>
      )}
    </div>
  );
};

export default BulkCustomerInput;
